import React from 'react'
import styled from 'styled-components'
import SidebarNavButton from 'Event/template/Cards/Dashboard/Sidebar/SidebarItem/SidebarNav/SidebarNavButton'
import NewSidebarNavButton from 'Event/template/Cards/Dashboard/Sidebar/SidebarItem/SidebarNav/NewSidebarNavButton'
import EditModeOnly from 'Event/Dashboard/editor/views/EditModeOnly'
import VisibleOnMatch from 'Event/attendee-rules/VisibleOnMatch'
import {DragDropContext, Droppable, DropResult} from 'react-beautiful-dnd'
import {useEditMode} from 'Event/EditModeProvider'
import {orderedIdsByPosition, createPositions} from 'lib/list'
import {RemoveButton} from 'organization/Event/DashboardConfig/ConfigDialog'
import {useEditSidebarItem} from 'Event/template/Cards/Dashboard/Sidebar/SidebarItem'
import Section from 'Event/template/Cards/Dashboard/Sidebar/Section'
import Grid from '@material-ui/core/Grid'
import {SidebarNavProps, SIDEBAR_NAV} from 'Event/template'
import Scheduled from 'lib/ui/layout/Scheduled'
import {useRemoveTranslations} from 'Event/LanguageProvider'
import {REMOVE, useSaveTemplate} from 'Event/TemplateUpdateProvider'
import {useAttendeeVariables} from 'Event'

export const createSidebarNav = (): SidebarNavProps => ({
  type: SIDEBAR_NAV,
  buttons: {},
})

export default function SidebarNav(props: SidebarNavProps & {id: string}) {
  return (
    <Section>
      <Content {...props} />
    </Section>
  )
}

function Content(props: SidebarNavProps & {id: string}) {
  const isEditMode = useEditMode()
  const {buttons} = props
  const v = useAttendeeVariables()

  const hasButtons = Object.keys(buttons).length > 0
  if (!hasButtons) {
    return (
      <EditModeOnly>
        <RemoveSidebarNavButton {...props} />
        <StyledNewSidebarNavButton listId={props.id} />
      </EditModeOnly>
    )
  }
  const ids = orderedIdsByPosition(buttons)

  const buttonComponents = ids.map((id, index) => {
    const button = buttons[id]
    return (
      <VisibleOnMatch rules={button.rules} key={id}>
        <Scheduled component={button}>
          <StyledGrid item xs={12} md={button.size}>
            <SidebarNavButton
              {...button}
              nav={props}
              listId={props.id}
              id={id}
              backgroundColor={button.backgroundColor}
              hoverBackgroundColor={button.hoverBackgroundColor}
              textColor={button.textColor}
              borderWidth={button.borderWidth}
              borderRadius={button.borderRadius}
              borderColor={button.borderColor}
              index={index}
              text={v(button.text)}
            />
          </StyledGrid>
        </Scheduled>
      </VisibleOnMatch>
    )
  })

  if (!isEditMode) {
    return <>{buttonComponents}</>
  }

  return <DroppleList {...props}>{buttonComponents}</DroppleList>
}

function RemoveSidebarNavButton(props: SidebarNavProps & {id: string}) {
  const saveTemplate = useSaveTemplate()
  const removeTranslations = useRemoveTranslations()

  const buttonKeys = Object.keys(props.buttons)

  const remove = () => {
    saveTemplate({
      sidebarItems: {
        [props.id]: REMOVE,
      },
      localization: removeTranslations(
        buttonKeys.flatMap((id) => [`${id}_text`]),
      ),
    })
  }

  return (
    <RemoveButton onClick={remove} size="large">
      Remove Buttons
    </RemoveButton>
  )
}

function DroppleList(
  props: SidebarNavProps & {children: React.ReactElement[]; id: string},
) {
  const handleDrag = useHandleDrag(props)

  return (
    <>
      <RemoveSidebarNavButton {...props} />
      <DragDropContext onDragEnd={handleDrag}>
        <Droppable droppableId="sidebar_nav_buttons">
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {props.children}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
        <StyledNewSidebarNavButton listId={props.id} />
      </DragDropContext>
    </>
  )
}

function useHandleDrag(props: SidebarNavProps) {
  const {update} = useEditSidebarItem()

  return (result: DropResult) => {
    const {destination, source} = result

    if (!destination) {
      return
    }
    const ids = orderedIdsByPosition(props.buttons)
    const [removed] = ids.splice(source.index, 1)
    ids.splice(destination.index, 0, removed)

    update({
      buttons: createPositions(ids),
    })
  }
}

const StyledGrid = styled(Grid)`
  margin: 0 auto;
`

const StyledNewSidebarNavButton = styled(NewSidebarNavButton)`
  margin-top: ${(props) => props.theme.spacing[6]} !important;
  margin-bottom: ${(props) => props.theme.spacing[5]}!important;
`
